// biome-ignore-all lint/suspicious/noExplicitAny: We need any here
//
// The PURE part of the focalazy translation API — no React, no browser globals —
// shared by the frontend (`@faceup/localization` re-exports it) and the backend.
// Moved here from packages/localization/src/TypedIntl.tsx so Node consumers
// don't pull React in. The Node focalazy fetch client that used to sit beside
// it now lives in apps/backend/src/translations/focalazyClient.ts (its only
// consumer), keeping this package pure and browser-safe.

export type Message<K extends string, W extends string> = {
  id: string
  defaultMessage: string
  description?: string
  values?: { [key in K]: string | true }
  wrappers?: { [key in W]: string | true }
}

// Every entry must be a Message<> object — string shorthand is not accepted.
// Call sites that used to spread enum-derived string arrays need to map them
// to `{ id, defaultMessage }` first.
type ObjectFromList<K extends string, W extends string, T extends readonly Message<K, W>[]> = {
  [P in T[number]['id']]: Extract<T[number], { id: P }>
}

const listToObject = <K extends string, W extends string, T extends readonly Message<K, W>[]>(
  namespace: string,
  list: T
): ObjectFromList<K, W, T> =>
  list.reduce(
    (acc, entry) => ({
      ...acc,
      [entry.id]: { ...entry, id: `${namespace}.${entry.id}` },
    }),
    {} as ObjectFromList<K, W, T>
  )

// ---------------------------------------------------------------------------
// Enum-keyed defineMessages (1D / 2D / matrix).
//
// Lets a map be keyed by an enum (or string-union via a values tuple) with
// exhaustiveness enforced, the source copy written inline, and shared
// values/wrappers declared once via a wrapper on the enum argument. The runtime
// object stays keyed by the RAW enum value; the generated `id` keeps each
// segment's casing (enum-derived leaves stay PascalCase) and only forces the
// whole id's first char lowercase (see `buildFullId`).
// See .claude/specs/definemessages-enum-maps.md.
// ---------------------------------------------------------------------------

type EnumLike = Record<string, string>

type ValuesOf<E> = E extends readonly (infer V extends string)[]
  ? V
  : E extends EnumLike
    ? E[keyof E]
    : never

type Meta<K extends string = never, W extends string = never> = {
  values?: { [key in K]: string | true }
  wrappers?: { [key in W]: string | true }
}

// The built message handed to consumers. `values`/`wrappers` are PRESENT
// (required) only when K/W are non-never — not optional like Message<K,W> — so
// FormattedMessage's `M extends { wrappers: any }` branch fires and requires the
// matching prop. (Compile-verified.)
type BuiltMessage<K extends string, W extends string> = {
  id: string
  defaultMessage: string
  description?: string
} & ([K] extends [never] ? unknown : { values: { [key in K]: string | true } }) &
  ([W] extends [never] ? unknown : { wrappers: { [key in W]: string | true } })

// Runtime symbols double as type-level brands so the overloads can tell a
// decorated enum apart from a bare one. Module-private (not exported).
const VARIABLES = Symbol('faceup.localization.withVariables')
const PER_FIELD_VARIABLES = Symbol('faceup.localization.withPerFieldVariables')
const MATRIX = Symbol('faceup.localization.withMatrix')

type WithVariables<E, K extends string, W extends string> = E & {
  readonly [VARIABLES]: Meta<K, W>
}
type WithPerFieldVariables<E, S> = E & { readonly [PER_FIELD_VARIABLES]: S }
type WithMatrix<O, I, K extends string, W extends string> = O & {
  readonly [MATRIX]: { inner: I; meta: Meta<K, W> }
}

// Attached to `defineMessages` as members below (defineMessages.withVariables
// etc.); not exported standalone.

/** Declare `values`/`wrappers` shared by every member of a 1D enum (or values-tuple) map. */
const withVariables = <
  E extends EnumLike | readonly string[],
  K extends string = never,
  W extends string = never,
>(
  enumObj: E,
  meta: Meta<K, W>
) => ({ ...enumObj, [VARIABLES]: meta }) as WithVariables<E, K, W>

/** Declare `values`/`wrappers` on specific sub-keys (fields) of a 2D enum map. */
const withPerFieldVariables = <E extends EnumLike, S extends Record<string, Meta<string, string>>>(
  enumObj: E,
  shape: S
) => ({ ...enumObj, [PER_FIELD_VARIABLES]: shape }) as WithPerFieldVariables<E, S>

/** Two-enum (nested) map: outer x inner, with shared cell `values`/`wrappers`. */
const withMatrix = <
  O extends EnumLike,
  I extends EnumLike,
  K extends string = never,
  W extends string = never,
>(
  outer: O,
  inner: I,
  meta?: Meta<K, W>
) => ({ ...outer, [MATRIX]: { inner, meta: meta ?? {} } }) as WithMatrix<O, I, K, W>

// Extract the declared value/wrapper key set for a sub-key from a
// withPerFieldVariables descriptor `S`. Matches only when `values`/`wrappers` is actually present
// (every object structurally matches `Meta<infer, infer>`, so inferring from
// that would wrongly yield `string` for an undeclared slot).
type MetaValues<S, K> = K extends keyof S
  ? S[K] extends { values: infer V }
    ? keyof V & string
    : never
  : never
type MetaWrappers<S, K> = K extends keyof S
  ? S[K] extends { wrappers: infer V }
    ? keyof V & string
    : never
  : never

// MUST match _translationAst.mjs `buildFullId` byte-for-byte (focalazy leaf-key
// rule). Segments keep their casing; only the whole id's first char is forced
// lowercase. Applied only to the id, never to the keys the returned object is
// indexed by.
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)
const buildFullId = (namespace: string, segments: string[]) =>
  lowerFirst(`${namespace}.${segments.join('.')}`)

// One recursive builder for 1D / 2D / matrix. Object value -> recurse (append a
// segment); string value -> leaf message. Keyed by the raw map keys.
const buildEnumMessages = (namespace: string, decorated: any, map: any): any => {
  const shape = decorated[PER_FIELD_VARIABLES]
  const uniformMeta = decorated[VARIABLES] ?? decorated[MATRIX]?.meta
  const build = (node: any, segments: string[]): any => {
    // A `null` leaf means "this member deliberately has no message for this
    // sub-key". Keep it null at runtime (the return type maps it to `null` too),
    // and the extractor skips it — no id is generated. Guard before the object
    // branch: `typeof null === 'object'`, so `Object.keys(null)` would throw.
    if (node === null) {
      return null
    }
    if (typeof node === 'string') {
      const leafKey = segments[segments.length - 1]
      const meta = uniformMeta ?? (leafKey === undefined ? undefined : shape?.[leafKey])
      return {
        id: buildFullId(namespace, segments),
        defaultMessage: node,
        ...(meta?.values ? { values: meta.values } : {}),
        ...(meta?.wrappers ? { wrappers: meta.wrappers } : {}),
      }
    }
    return Object.fromEntries(
      Object.keys(node).map(key => [key, build(node[key], [...segments, key])])
    )
  }
  return build(map, [])
}

// 2D + meta (withPerFieldVariables) — branded overloads first; a
// WithPerFieldVariables/WithMatrix/WithVariables value also structurally extends
// EnumLike, so its overload must win. The whole map `M` is inferred (so sub-keys
// stay literal, not widened to `string`); per-sub meta is read from the
// withPerFieldVariables descriptor `S`.
function defineMessagesImpl<
  E extends EnumLike,
  S extends Record<string, Meta<string, string>>,
  M extends Record<ValuesOf<E>, Record<string, string>>,
>(
  namespace: string,
  enumWithPerFieldVariables: WithPerFieldVariables<E, S>,
  map: M & Record<ValuesOf<E>, Record<string, string>>
): {
  [Member in keyof M]: {
    [Sub in keyof M[Member]]: BuiltMessage<MetaValues<S, Sub>, MetaWrappers<S, Sub>>
  }
}
// matrix (withMatrix) — outer x inner. Both keys are known enum unions, so the
// direct nested Record both enforces exhaustiveness on each axis AND indexes
// cleanly (no inferred sub-key to widen).
function defineMessagesImpl<
  O extends EnumLike,
  I extends EnumLike,
  K extends string,
  W extends string,
>(
  namespace: string,
  enumWithMatrix: WithMatrix<O, I, K, W>,
  map: Record<ValuesOf<O>, Record<ValuesOf<I>, string>>
): Record<ValuesOf<O>, Record<ValuesOf<I>, BuiltMessage<K, W>>>
// 1D + shared meta (withVariables)
function defineMessagesImpl<
  E extends EnumLike | readonly string[],
  K extends string,
  W extends string,
>(
  namespace: string,
  enumWithVariables: WithVariables<E, K, W>,
  map: Record<ValuesOf<E>, string>
): Record<ValuesOf<E>, BuiltMessage<K, W>>
// 2D plain — sub-keys inferred (literal) from the object map; the `& Record<...>`
// intersection enforces outer-enum exhaustiveness while M keeps the literal keys.
// A sub-key may be `null` for a member that has no copy for it — the shape then
// stays uniform (every member lists every sub-key) and the return type maps that
// slot to `null`, NOT to `BuiltMessage`. So indexing `messages[member].sub` needs
// a `? :` guard ONLY when at least one member is null for that sub-key; if every
// member has copy, the union collapses to plain `BuiltMessage` (no guard).
function defineMessagesImpl<
  E extends EnumLike | readonly string[],
  M extends Record<ValuesOf<E>, Record<string, string | null>>,
>(
  namespace: string,
  enumOrValues: E,
  map: M & Record<ValuesOf<E>, Record<string, string | null>>
): {
  [Member in keyof M]: {
    [Sub in keyof M[Member]]: M[Member][Sub] extends null ? null : BuiltMessage<never, never>
  }
}
// 1D plain — the value IS the defaultMessage
function defineMessagesImpl<E extends EnumLike | readonly string[]>(
  namespace: string,
  enumOrValues: E,
  map: Record<ValuesOf<E>, string>
): Record<ValuesOf<E>, BuiltMessage<never, never>>
// legacy list — unchanged
function defineMessagesImpl<K extends string, W extends string, T extends readonly Message<K, W>[]>(
  namespace: string,
  messages: T
): ObjectFromList<K, W, T>
function defineMessagesImpl(namespace: string, second: any, third?: any): any {
  // Discriminate by arg COUNT, not `Array.isArray(second)`: the legacy list form
  // is 2-arg (no map) with an array second arg, but a 3-arg enum-keyed call can
  // ALSO pass an array as second (a values tuple / `zodEnum.options`), and that
  // must build from the map (third), not be mistaken for a legacy list.
  if (third === undefined && Array.isArray(second)) {
    return listToObject(namespace, second)
  }
  return buildEnumMessages(namespace, second, third)
}

// Attach the enum-map decorators as members so call sites only import
// `defineMessages` (defineMessages.withVariables / .withPerFieldVariables /
// .withMatrix). The explicit intersection preserves every overload above.
export const defineMessages: typeof defineMessagesImpl & {
  withVariables: typeof withVariables
  withPerFieldVariables: typeof withPerFieldVariables
  withMatrix: typeof withMatrix
} = Object.assign(defineMessagesImpl, { withVariables, withPerFieldVariables, withMatrix })
